import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'retreat', label: 'Elevated Escape' },
  { id: 'outdoors', label: 'Outdoors' },
  { id: 'events', label: 'Community Events' },
];

const GalleryPage = () => {
  usePageTitle('Gallery');
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);
  const [heroRef, heroVisible] = useScrollReveal();
  const [gridRef, gridVisible] = useScrollReveal();

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const res = await fetch(`${API}/gallery?visible_only=true`);
        if (res.ok) setPhotos(await res.json());
      } catch (err) { console.error('Error loading gallery:', err); }
      setLoading(false);
    };
    fetchPhotos();
  }, []);

  const filtered = filter === 'all' ? photos : photos.filter(p => p.category === filter);

  const openLightbox = (idx) => setLightbox(idx);
  const closeLightbox = () => setLightbox(null);
  const prev = () => setLightbox(i => (i > 0 ? i - 1 : filtered.length - 1));
  const next = () => setLightbox(i => (i < filtered.length - 1 ? i + 1 : 0));

  return (
    <div data-testid="gallery-page">
      {/* Hero */}
      <section ref={heroRef} className={`bg-brand-dark py-20 lg:py-24 transition-all duration-700 ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl lg:text-4xl font-semibold text-white mb-4">Gallery</h1>
            <p className="text-lg text-gray-400">Moments from our community. Events, retreats, and the connections that keep us going.</p>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="bg-brand-light py-6 border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => setFilter(cat.id)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${filter === cat.id ? 'bg-brand-dark text-white' : 'bg-white text-brand-dark hover:bg-gray-100'}`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Grid */}
      <section className="bg-white py-12 lg:py-16">
        <div ref={gridRef} className={`mx-auto max-w-7xl px-6 lg:px-8 transition-all duration-700 ease-out ${gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {loading ? (
            <p className="text-center text-gray-400 py-12">Loading gallery...</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-12">No photos in this category yet.</p>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4" data-testid="gallery-grid">
              {filtered.map((photo, idx) => (
                <div
                  key={photo.id}
                  className="break-inside-avoid mb-4 group cursor-pointer overflow-hidden"
                  onClick={() => openLightbox(idx)}
                  data-testid={`gallery-photo-${idx}`}
                >
                  <div className="relative">
                    <img
                      src={photo.src}
                      alt={photo.alt}
                      className="w-full h-auto group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-testid="lightbox" onClick={closeLightbox}>
          <button onClick={(e) => { e.stopPropagation(); closeLightbox(); }} className="absolute top-4 right-4 text-white/70 hover:text-white z-10 p-2" data-testid="lightbox-close">
            <X className="w-8 h-8" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 text-white/70 hover:text-white z-10 p-2" data-testid="lightbox-prev">
            <ChevronLeft className="w-10 h-10" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 text-white/70 hover:text-white z-10 p-2" data-testid="lightbox-next">
            <ChevronRight className="w-10 h-10" />
          </button>
          <img
            src={filtered[lightbox]?.src}
            alt={filtered[lightbox]?.alt}
            className="max-h-[85vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="absolute bottom-6 left-0 right-0 text-center text-white/60 text-sm">{filtered[lightbox]?.alt}</p>
        </div>
      )}

      {/* CTA */}
      <section className="bg-brand-light py-12">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <p className="text-brand-gold/80 text-sm font-medium mb-4">You are not alone.</p>
          <p className="text-gray-600 mb-6">Want to be part of our next event?</p>
          <Link to="/events" className="inline-block bg-brand-dark px-8 py-3 text-white font-medium hover:opacity-90 transition-opacity" data-testid="gallery-cta">
            View Upcoming Events
          </Link>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;
